/**
 * Comparison page data - one entry per /compare/<slug> route.
 *
 * Editorial guideline: never make false or unsourced claims about
 * competitors. Frame each comparison as "what Restack uniquely offers"
 * and "when the other tool is the right one" rather than competitor
 * bashing. The reader is doing a BOFU verification search; honesty plus
 * clarity converts better than spin. No em dashes (use " - ").
 *
 * Data model (all fields optional unless noted):
 *   slug, competitorName            - required
 *   metaTitle, metaDescription      - SEO
 *   hero: { eyebrow, headline, subhead }
 *   intro                           - lead paragraph(s); split on "\n\n"
 *   comparisons: [                  - one OR more comparison blocks
 *     { heading?, verdict?, body?, competitorLabel, rows: [{feature, restack, competitor}] }
 *   ]
 *   differentiators: { heading, items: [{title, description}] }
 *   whenCompetitor                  - text after "When <X> is the right tool"
 *   whenRestack                     - text after "When Restack"
 *   faq: [{q, a}]                   - competitor-scoped questions, rendered
 *                                     above the shared product FAQ and emitted
 *                                     as FAQPage JSON-LD
 *   close                           - shared closing paragraph
 *
 * Adding a new competitor = one new entry here. No new component.
 *
 * FAQ rule: every answer below is composed from copy that already appears in
 * the same entry (intro, body, differentiators, whenCompetitor/whenRestack).
 * An FAQ is not a licence to make a claim the comparison itself does not.
 */

export const comparisons = {
  'restack-vs-claude': {
    slug: 'restack-vs-claude',
    competitorName: 'Claude',

    metaTitle: 'Restack vs Claude - Two Comparisons for Private Equity Decks',
    metaDescription:
      'There are two Claudes: the chat app and the PowerPoint add-in. Restack runs on Claude and wins both comparisons on different grounds - the last-mile gap against the chat, and vertical deal-team depth against the add-in.',

    hero: {
      eyebrow: 'Two Claudes, two answers',
      headline: 'Restack vs Claude',
      subhead:
        '"How is this different from Claude?" has two answers, because there are two Claudes. Restack wins both, on different grounds.',
    },

    intro:
      'Start here: Restack runs on Claude. This is not Restack instead of Claude - keep using Claude to think. The question is what happens after the thinking, when you need an actual deck. And the answer depends on which Claude you mean.',

    comparisons: [
      {
        heading: 'Restack vs the Claude app (claude.ai / Cowork)',
        visual: 'research',
        verdict:
          "Claude's chat gives you the thinking, as text. You still rebuild it into a deck by hand. Restack does that work inside your real PowerPoint.",
        body:
          'The objection we hear most: "I can just prompt better and get the same output." Better prompting gets you better content. It does not get you a finished deck. The chat hands you text in a window, and someone still rebuilds it into your template, on brand, on grid, as an editable .pptx. That is the 4 to 6 hours per deck, and no prompt closes it, because the chat does not live in your file. Restack works inside your actual PowerPoint: it builds and edits the real slides, and it can take the 40-slide deck you already have and refresh it in place with new numbers. Prompting improves the thinking. It cannot move the output into your deck.',
        competitorLabel: 'Claude app (chat)',
        rows: [
          { feature: 'Output', restack: 'Edited slides in your live .pptx', competitor: 'Text in a chat window' },
          { feature: 'Getting to a deck', restack: 'Built and edited in the file directly', competitor: 'Copy, paste, reformat by hand' },
          { feature: 'Your existing 40-slide deck', restack: 'Refresh it in place, same template', competitor: 'Start over' },
          { feature: 'Brand and template', restack: 'Enforced against your slide master', competitor: 'You apply it manually' },
        ],
      },
      {
        heading: 'Restack vs Claude for PowerPoint (the add-in)',
        visual: 'vertical',
        verdict:
          'Claude for PowerPoint is a capable editor, but a horizontal one. Restack is the vertical, deal-team version, built around the work you repeat.',
        body:
          'We will not pretend otherwise: Claude for PowerPoint works inside PowerPoint, respects your template, and edits slides. The difference is not the surface, it is the depth. It is built for everyone and every document. Restack is built for Private Equity deal teams - IC-memo and board-deck structure, the Pyramid Principle, firm brand and slide-master enforcement, and the one job a deal team repeats every week: refreshing the same deck with a new data wave, the same way every time. A horizontal tool does not optimize for that. Restack is built around it.',
        competitorLabel: 'Claude for PowerPoint',
        rows: [
          { feature: 'Built for', restack: 'Private Equity deal teams', competitor: 'Everyone, every document' },
          { feature: 'Deck logic', restack: 'IC memos, board pre-reads, SteerCos, action titles, firm brand rules', competitor: 'General' },
          { feature: 'Recurring refresh', restack: 'One instruction: update this deck with the new wave, keep the template', competitor: 'Re-prompt each time' },
          { feature: 'Depth of fit', restack: 'Deal-team workflows, deep', competitor: 'Broad' },
        ],
      },
    ],

    faq: [
      {
        q: 'Does Restack replace Claude?',
        a: 'No. Restack runs on Claude. Keep using Claude to think. Restack is what happens after the thinking, when you need an actual deck: it builds and edits the real slides inside your PowerPoint, on your brand.',
      },
      {
        q: 'Could better prompting close the gap?',
        a: 'Better prompting gets you better content. It does not get you a finished deck. The chat hands you text in a window, and someone still rebuilds it into your template, on brand, on grid, as an editable .pptx.',
      },
    ],

    close:
      'Either way, Restack runs on Claude. Use Claude to think. Use Restack to build the deck - inside your real PowerPoint, on your brand, without the rebuild.',
  },

  'restack-vs-rogo': {
    slug: 'restack-vs-rogo',
    competitorName: 'Rogo',

    metaTitle: 'Restack vs Rogo - Deck Production vs Finance Data Engine',
    metaDescription:
      'Rogo is a finance data and modeling platform that also makes decks. Restack is a deck-production editor for private equity. Here is how they compare.',

    hero: {
      eyebrow: 'Different center of gravity',
      headline: 'Restack vs Rogo',
      subhead:
        'Rogo is a finance data and modeling platform that also makes decks. Restack is a deck-production editor for private equity. Different center of gravity.',
    },

    intro:
      "Rogo is a serious platform built around finance data - models, CIMs, precedent analysis, your firm's data warehouse - and it generates decks at the end of that pipeline. Restack starts at the other end: the deck itself. If your hard part is the financial model, Rogo is built for that. If your hard part is turning analysis into a brand-compliant, board-ready deck across private equity work, not just banking, that is Restack.",

    comparisons: [
      {
        competitorLabel: 'Rogo',
        rows: [
          { feature: 'Center of gravity', restack: 'Deck production and craft', competitor: 'Finance data, models, research' },
          { feature: 'Audience', restack: 'Private Equity deal teams, boutiques, value creation', competitor: 'Investment banking and finance' },
          { feature: 'Surface', restack: 'Editor inside your live .pptx; you stay in the loop', competitor: 'Platform with Office integrations; agents generate end-to-end' },
          { feature: 'Best when', restack: 'The hard part is the slide - brand, structure, recurring refresh', competitor: 'The hard part is the model and the data' },
          { feature: 'Control', restack: 'You drive the edit slide by slide', competitor: 'Agent generates, you review' },
        ],
      },
    ],

    differentiators: {
      heading: 'The real difference',
      items: [
        {
          title: 'Deck-craft, not a data engine',
          visual: 'slideBuild',
          description:
            "Rogo's gravity is the financial model. Restack's is the deliverable - the brand-compliant, structured deck a partner opens Tuesday morning.",
        },
        {
          title: 'An editor, not a generator you review',
          visual: 'templateSwap',
          description:
            'You work inside the actual file, slide by slide, with granular control - not a finished deck handed over the wall to check.',
        },
        {
          title: 'Private Equity, not just banking',
          visual: 'proposal',
          description:
            'IC memos, board pre-reads, SteerCos, portco reviews - the full breadth of deal-team and consulting decks.',
        },
      ],
    },

    whenCompetitor:
      'Heavy financial modeling, CIMs, and IB data workflows tied to a firm data warehouse.',
    whenRestack:
      'Deck production across private equity where slide-craft, brand compliance, and recurring refresh are the bottleneck.',

    faq: [
      {
        q: 'Do Restack and Rogo do the same job?',
        a: "Not really. Rogo is built around finance data - models, CIMs, precedent analysis, your firm's data warehouse - and it generates decks at the end of that pipeline. Restack starts at the other end: the deck itself.",
      },
      {
        q: 'Which one fits our team?',
        a: 'If your hard part is the financial model, Rogo is built for that. If your hard part is turning analysis into a brand-compliant, board-ready deck across private equity work, not just banking, that is Restack.',
      },
    ],
  },

  'restack-vs-hebbia': {
    slug: 'restack-vs-hebbia',
    competitorName: 'Hebbia',

    metaTitle: 'Restack vs Hebbia | Research Engine + Deck Builder',
    metaDescription:
      'Hebbia finds the answer in your data room. Restack turns it into the board-ready deck. Different categories: here is why the strongest teams use both.',

    hero: {
      eyebrow: 'Brains and hands, not either-or',
      headline: 'Restack vs Hebbia',
      subhead:
        'Hebbia finds the answer in your data room. Restack turns it into the deck. Use both.',
    },

    intro:
      'This one is simple - they are not competitors. Hebbia is an AI research engine: point it at a data room or your document corpus and it answers complex questions with citations. It is the diligence layer, the brains. Restack is the production layer, the hands: it takes what you have concluded and builds the board-ready, brand-compliant deck inside PowerPoint. The strongest teams use Hebbia to get to the answer and Restack to present it.',

    comparisons: [
      {
        competitorLabel: 'Hebbia',
        rows: [
          { feature: 'The job', restack: 'Build and refresh the deck', competitor: 'Search, retrieve, and synthesize from documents and data rooms' },
          { feature: 'Output', restack: 'A brand-compliant .pptx deliverable', competitor: 'Cited answers and analysis' },
          { feature: 'Layer', restack: 'Hands - produce and present', competitor: 'Brains - find and understand' },
          { feature: 'Where it lives', restack: 'Inside your live PowerPoint', competitor: 'Across your document corpus' },
        ],
      },
    ],

    differentiators: {
      heading: 'The real difference',
      items: [
        {
          title: 'A category difference, not a competition',
          visual: 'research',
          description:
            'Hebbia answers "what is in these documents." Restack answers "now make it a board deck." Brains and hands, not either-or.',
        },
        {
          title: 'Better together',
          visual: 'connectors',
          description:
            'Hebbia surfaces the insight from the data room. You make the call. Restack turns it into the IC memo or board deck, without rebuilding a single slide by hand.',
        },
      ],
    },

    whenCompetitor:
      'Diligence, document search, red-flag scans, and synthesis across a data room.',
    whenRestack:
      'Turning any of that into the deliverable. Most teams that use one have a use for the other.',

    faq: [
      {
        q: 'Is Restack a replacement for Hebbia?',
        a: 'No, they are different categories. Hebbia is an AI research engine: point it at a data room and it answers complex questions with citations. Restack is the production layer that turns what you concluded into the board-ready, brand-compliant deck.',
      },
      {
        q: 'Can we run both?',
        a: 'Yes, and the strongest teams do. Hebbia surfaces the insight from the data room. You make the call. Restack turns it into the IC memo or board deck, without rebuilding a single slide by hand.',
      },
    ],
  },

  'restack-vs-chatgpt': {
    slug: 'restack-vs-chatgpt',
    competitorName: 'ChatGPT',

    metaTitle: 'Restack vs ChatGPT - Why Slides Need More Than a Chatbot',
    metaDescription:
      'ChatGPT drafts text. Restack builds slides directly in PowerPoint, on your slide master, with cited research. Here is when each is the right tool for private equity work.',

    hero: {
      eyebrow: 'General AI vs PowerPoint-native AI',
      headline: 'Restack vs ChatGPT',
      subhead:
        'ChatGPT drafts text. Restack builds slides - directly inside your PowerPoint deck, on your slide master, with cited research.',
    },

    intro:
      'ChatGPT is a remarkable general-purpose AI: ideation, drafting, research synthesis, even web search. But when the deliverable is a deck for a client or investment committee, the gap shows. ChatGPT outputs text; you copy-paste into PowerPoint, you reformat to your template, you find sources, you build the chart, you add the citations. Restack does all of that inside PowerPoint, on your slide master, with cited research from up to 100 sources per slide.',

    comparisons: [
      {
        competitorLabel: 'ChatGPT',
        rows: [
          { feature: 'Where the AI runs', restack: 'Inside PowerPoint task pane', competitor: 'Web app or chat interface' },
          { feature: 'Native output', restack: 'Slides - title, body, chart, citations, formatting', competitor: 'Text - paste into other tools' },
          { feature: 'Reads your slide master', restack: 'Yes - output lands on your template', competitor: 'No' },
          { feature: 'Citations', restack: 'Up to 100 sources per slide, page-and-paragraph traceable', competitor: 'Inline web links (Plus and Team plans)' },
          { feature: 'Built for strategy frameworks', restack: '20+ frameworks: TAM/SAM/SOM, Porter, BCG, McKinsey 7S', competitor: 'General-purpose; frameworks via prompting' },
          { feature: 'Direct deck editing', restack: 'Edit in PowerPoint as the AI builds', competitor: 'Output, copy, paste, reformat' },
        ],
      },
    ],

    differentiators: {
      heading: 'What Restack does best',
      items: [
        {
          title: "Slides aren't text",
          visual: 'charts',
          description:
            "A market-sizing slide isn't three paragraphs. It's a layout - TAM bar chart, methodology callout, citation footer, CAGR annotation. ChatGPT writes the words. Restack builds the slide.",
        },
        {
          title: "Your firm's template is half the work",
          visual: 'governance',
          description:
            "Reformatting AI output to your firm's style - fonts, colors, margins, disclaimer placement - is what eats the hours. Restack reads your slide master and builds on top of it. One pass.",
        },
        {
          title: 'Citations the IC can defend',
          visual: 'citations',
          description:
            'Investment committees ask "where did that number come from?" Restack ships every figure with a page-and-paragraph citation - point at the exact sentence in the source PDF, not just the article URL.',
        },
      ],
    },

    whenCompetitor:
      'Ideation, drafting, and research synthesis - anything that does not end as a deck.',
    whenRestack:
      'Shipping a brand-compliant, cited deck inside PowerPoint. They are complementary tools at different layers, and most teams use both.',

    faq: [
      {
        q: 'Can ChatGPT not just write my slides?',
        a: 'It writes the text. When the deliverable is a deck for a client or investment committee, someone still copy-pastes into PowerPoint, reformats to the template, finds the sources, builds the chart, and adds the citations. Restack does that inside PowerPoint, on your slide master, with cited research.',
      },
      {
        q: 'Should we stop using ChatGPT?',
        a: 'No. ChatGPT is the right tool for ideation, drafting, and research synthesis - anything that does not end as a deck. They are complementary tools at different layers, and most teams use both.',
      },
    ],
  },
};

export const comparisonSlugs = Object.keys(comparisons);
